import _isFinite from "lodash/isFinite";
import PropTypes from "prop-types";
import React from "react";

import { makeClassNameFunctionForModule } from "../utils/cssModules";
import PageLabel from "./PageLabel";
import styles from "../styles/components/MiniPage.css";

const classNames = makeClassNameFunctionForModule(styles);

const _pageShape = {
    pageLabel: PropTypes.string.isRequired,
    image: PropTypes.shape({
        files: PropTypes.objectOf(
            PropTypes.shape({
                imageUrl: PropTypes.string.isRequired
            })
        ).isRequired
    }).isRequired,
    name: PropTypes.string.isRequired,
    color: PropTypes.string
};

/**
 * Renders a page image with a drop shadow and the page info (pageLabel & name) underneath. Also supports an optional
 * numeric badge and an optional overlay on hover.
 *
 * This component takes up the full size of its container. The image will be sized to fit the width of the component
 * while maintaining its aspect ratio. If the image is too tall to fit, it will be clipped.
 *
 * @param className optional CSS classes for the outermost element. These will be merged with the default classes for
 * that element.
 * @param image the page image to display.
 * @param isValid if `numCtas` is specified, this property determines the color of the CTA badge.
 * @param pageLabel the page pageLabel. This is rendered in a `PageLabel` component.
 * @param color the color of the page pageLabel. This is passed to the `PageLabel` component.
 * @param name the name of the page. This will be truncated to the width of the component.
 * @param numCtas the number of CTAs associated with this page. If specified, a badge will be displayed in the top right
 * corner of the component. If omitted, nil, or NaN, the badge will not be displayed.
 * @param renderOverlay a function that renders the overlay to display when the user hovers over this component. If this
 * is not given, there will be no overlay on hover.
 * @param props additional props to add to the outermost component. You can use this to specify `onClick` behavior, for
 * example.
 * @returns {*}
 * @constructor
 */
export default class MiniPage extends React.Component {
    static pageShape = _pageShape;

    static propTypes = {
        page: PropTypes.shape(_pageShape).isRequired,
        isValid: PropTypes.bool,
        numCtas: PropTypes.number,
        renderOverlay: PropTypes.func
    };

    static defaultProps = {
        isValid: true,
        numCtas: null,
        renderOverlay: null
    };

    element = null;

    storeElement = element => (this.element = element);

    hover = () => {
        if (!this.element) {
            return;
        }
        const rect = this.element.getBoundingClientRect();
        this.element.style.cssText = `{ position: fixed, top: ${
            rect.top
        }px; left: ${rect.left}px; width: 700px; height: 700px }`;
    };

    clearHover = () => {
        if (!this.element) {
            return;
        }
        this.element.style = null;
    };

    render = () => {
        const {
            className,
            isValid,
            numCtas,
            page,
            renderOverlay,
            ...props
        } = this.props;
        const { color, image, name, pageLabel } = page;

        const imageUrl = image.files.original.imageUrl;
        const withCtas = _isFinite(numCtas);
        return (
            <div
                {...classNames(className, "miniPage", withCtas && "withCtas")}
                {...props}
                ref={this.storeElement}
                onMouseOver={this.hover}
                onMouseOut={this.clearHover}
            >
                {withCtas && (
                    <p
                        {...classNames(
                            "ctaBadge",
                            isValid ? "valid" : "invalid"
                        )}
                    >
                        {numCtas}
                    </p>
                )}

                <div className={styles.imageContainer}>
                    <img className={styles.image} src={imageUrl} />
                    {renderOverlay && (
                        <div className={styles.imageOverlay}>
                            {renderOverlay()}
                        </div>
                    )}
                </div>

                <div className={styles.pageInfo}>
                    <PageLabel
                        className={styles.pageLabel}
                        label={pageLabel}
                        color={color}
                    />
                    <p className={styles.pageName} title={name}>
                        {name}
                    </p>
                </div>
            </div>
        );
    };
}



// WEBPACK FOOTER //
// ./src/components/MiniPage.js